import React, { useState } from 'react';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Typography,
  Box,
  Button,
  Divider,
  CircularProgress,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import ArticleIcon from '@mui/icons-material/Article';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client/react';
import gql from 'graphql-tag';
import { useRouter } from 'next/router';
import { SvgIconComponent } from '@mui/icons-material';

const GET_UNREAD_COUNT = gql`
  query unreadNotificationCount {
    unreadNotificationCount
  }
`;

const GET_NOTIFICATIONS = gql`
  query inAppNotifications($offset: Int, $limit: Int) {
    inAppNotifications(offset: $offset, limit: $limit) {
      id
      contentType
      contentId
      contentName
      contentUrl
      contentCity
      contentShortDescription
      isRead
      savedFilter {
        id
        name
      }
      createdAt
    }
  }
`;

const MARK_READ = gql`
  mutation markNotificationRead($id: Int!) {
    markNotificationRead(id: $id) {
      id
      isRead
    }
  }
`;

const MARK_ALL_READ = gql`
  mutation markAllNotificationsRead {
    markAllNotificationsRead
  }
`;

interface ContentTypeConfig {
  icon: SvgIconComponent;
  label: string;
  color: string;
}

const contentTypeConfig: Record<string, ContentTypeConfig> = {
  actor: { icon: PersonIcon, label: 'Acteur', color: '#2c367e' },
  event: { icon: EventIcon, label: 'Événement', color: '#69b6a9' },
  article: { icon: ArticleIcon, label: 'Article', color: '#d96552' },
  recipe: { icon: RestaurantIcon, label: 'Recette', color: '#fcc100' },
  admin_message: { icon: MailOutlineIcon, label: 'Message', color: '#2c367e' },
};

function formatDate(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(/^\d+$/.test(dateStr) ? Number(dateStr) : dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffH = Math.floor(diffMs / 3600000);
  const diffD = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return "À l'instant";
  if (diffMin < 60) return `Il y a ${diffMin} min`;
  if (diffH < 24) return `Il y a ${diffH}h`;
  if (diffD < 7) return `Il y a ${diffD}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

interface SavedFilter {
  id: string | number;
  name: string;
}

interface Notification {
  id: string | number;
  contentType: string;
  contentId?: string | number;
  contentName?: string;
  contentUrl?: string;
  contentCity?: string;
  contentShortDescription?: string;
  isRead: boolean;
  savedFilter?: SavedFilter | null;
  createdAt?: string;
}

const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const router = useRouter();

  const { data: countData, refetch: refetchCount } = useQuery(GET_UNREAD_COUNT, {
    pollInterval: 60000,
    fetchPolicy: 'network-only',
  });

  const [fetchNotifs, { data: notifsData, loading }] = useLazyQuery(GET_NOTIFICATIONS, {
    variables: { limit: 30 },
    fetchPolicy: 'network-only',
  });

  const [markRead] = useMutation(MARK_READ);
  const [markAllRead] = useMutation(MARK_ALL_READ);

  const unreadCount: number = countData?.unreadNotificationCount || 0;
  const notifications: Notification[] = notifsData?.inAppNotifications || [];

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    fetchNotifs();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = async (notif: Notification) => {
    if (!notif.isRead) {
      await markRead({ variables: { id: parseInt(String(notif.id)) } });
      refetchCount();
    }
    handleClose();
    if (notif.contentUrl) {
      const targetUrl = notif.contentType === 'article' && notif.contentUrl.startsWith('/news/')
        ? notif.contentUrl.replace('/news/', '/article/')
        : notif.contentUrl;
      router.push(targetUrl);
    }
  };

  const handleMarkAllRead = async () => {
    await markAllRead();
    refetchCount();
    fetchNotifs();
  };

  const handleFilterClick = (e: React.MouseEvent, filterId: string | number) => {
    e.stopPropagation();
    handleClose();
    router.push(`/carte?filterId=${filterId}`);
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        color="inherit"
        onClick={handleOpen}
        aria-label="Notifications"
        sx={{ mr: 1 }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '0.7rem',
              minWidth: '18px',
              height: '18px',
            },
          }}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: '100vw', sm: 380 },
              maxHeight: 480,
              mt: 1,
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600 }}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Button
              size="small"
              startIcon={<DoneAllIcon />}
              onClick={handleMarkAllRead}
              sx={{ textTransform: 'none', fontSize: '0.8rem' }}
            >
              Tout marquer lu
            </Button>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 3, px: 2.5 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #2c367e 0%, #69b6a9 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 1.5,
              }}
            >
              <NotificationsIcon sx={{ fontSize: 30, color: 'white' }} />
            </Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: '#2c367e', mb: 0.5, fontSize: '1rem' }}
            >
              Ne rate aucun acteur ni événement&nbsp;!
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: '0.85rem', lineHeight: 1.4, mb: 1.5 }}
            >
              Crée une alerte sur tes critères&nbsp;: tu seras prévenu·e dès qu&apos;un nouveau contenu correspond.
            </Typography>
            <Box
              sx={{
                bgcolor: '#f5f7ff',
                borderLeft: '3px solid #69b6a9',
                borderRadius: 1,
                p: 1.25,
                mb: 2,
                textAlign: 'left',
              }}
            >
              <Typography
                variant="caption"
                sx={{ color: '#444', fontStyle: 'italic', fontSize: '0.78rem', lineHeight: 1.4 }}
              >
                Ex&nbsp;: «&nbsp;Reçois un email dès qu&apos;un nouvel événement
                {' '}<strong>Agriculture</strong> est publié près de
                {' '}<strong>La Rochelle</strong>&nbsp;»
              </Typography>
            </Box>
            <Button
              variant="contained"
              fullWidth
              onClick={() => {
                handleClose();
                router.push('/carte?openFilters=1');
              }}
              sx={{
                backgroundColor: '#2c367e',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                py: 1,
                '&:hover': { backgroundColor: '#1f2860' },
              }}
            >
              Créer ma première alerte →
            </Button>
          </Box>
        ) : (
          <List sx={{ py: 0, overflowY: 'auto', maxHeight: 380 }}>
            {notifications.map((notif) => {
              const config = contentTypeConfig[notif.contentType] || contentTypeConfig.actor;
              const Icon = config.icon;
              return (
                <React.Fragment key={notif.id}>
                  <ListItem
                    onClick={() => handleNotificationClick(notif)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: notif.isRead ? 'transparent' : 'rgba(44, 54, 126, 0.06)',
                      '&:hover': { bgcolor: 'action.hover' },
                      alignItems: 'flex-start',
                      py: 1.5,
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                      <Icon sx={{ color: config.color, fontSize: 24 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: notif.isRead ? 400 : 600,
                              flex: 1,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {notif.contentName}
                          </Typography>
                          {!notif.isRead && (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: '#2c367e',
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box component="span">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            {config.label} · {formatDate(notif.createdAt)}
                          </Typography>
                          {notif.contentCity && (
                            <Box
                              component="span"
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.3,
                                mt: 0.3,
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                              }}
                            >
                              <LocationOnIcon sx={{ fontSize: 14, color: '#69b6a9' }} />
                              {notif.contentCity}
                            </Box>
                          )}
                          {notif.contentShortDescription && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              component="span"
                              sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mt: 0.3,
                                fontStyle: 'italic',
                                fontSize: '0.7rem',
                                lineHeight: 1.3,
                              }}
                            >
                              {notif.contentShortDescription}
                            </Typography>
                          )}
                          {notif.savedFilter && (
                            <Box
                              component="span"
                              onClick={(e) => handleFilterClick(e, notif.savedFilter!.id)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.3,
                                mt: 0.3,
                                color: '#2c367e',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              <FilterAltIcon sx={{ fontSize: 14 }} />
                              {notif.savedFilter.name}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  <Divider component="li" />
                </React.Fragment>
              );
            })}
          </List>
        )}
      </Popover>
    </>
  );
};

export default NotificationBell;
